import { ConvertTokenError } from "@redotech/customer-portal-sdk/rpc/schema/convert-token-to-customer-auth";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { PurchaseChannel } from "@redotech/redo-model/warranties";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SetAuthContext } from "../contexts/auth";
import { SettingsContext } from "../contexts/settings";
import { SetUserContext } from "../contexts/user";

export interface UseLoginResponse {
  settings: CustomerPortalAppSettings;
  isPortalDisabled: boolean;
  currentPage: LoginPage;
  purchaseChannel: PurchaseChannel | undefined;
  channelName: string;
  onBackGiftClicked: () => void;
  onBackZipcodeClicked: () => void;
  onBackRegisterClicked: (() => void) | undefined;
  onBackRegisterOtpClicked: () => void;
  giftButtonClicked: () => void;
  emailButtonClicked: () => void;
  registerButtonClicked: () => void;
  toRetailRegistration: (channelName: string) => void;
  toOnlineRegistration: (channelName: string) => void;
  isMobile: boolean;
  errorMessage: string | undefined;
  isCreateGiftReturnLink: boolean;
}

const errorCodeToMessage: Record<ConvertTokenError, string> = {
  [ConvertTokenError.ExpiredToken]: "This link has expired",
  [ConvertTokenError.InvalidToken]: "Invalid token",
};

export enum LoginPage {
  EMAIL_LINK = "email-link",
  LANDING_PAGE = "landing-page",
  GIFT = "gift",
  REGISTER = "register",
  REGISTER_OTP = "register-otp",
}

/**
 * Get the initial page to display based on the search params
 *
 * Single order login:
 *  - Auto navigate to single order login, feature request for merchants
 *
 * Register only:
 *  - Auto navigate to register page, feature request for merchants
 *
 * Default:
 *  - Display one time passcode page
 *
 * @param params - The search params
 * @returns The initial page to display
 */
function getInitialPage(params: URLSearchParams): LoginPage {
  const startGiftReturn = params.get("start_gift_return");
  const registerOnly = params.get("register_only");
  if (registerOnly) {
    return LoginPage.REGISTER;
  }
  if (startGiftReturn) {
    return LoginPage.GIFT;
  }
  return LoginPage.LANDING_PAGE;
}

export function useLogin(): UseLoginResponse {
  const settings = useRequiredContext(SettingsContext);
  const rpcClient = useContext(RpcClientContext);
  const [params] = useSearchParams();
  const screen = useScreenType();

  const navigate = useNavigate();

  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const isCreateGiftReturnLink =
    params.get("create_gift_return_link") !== null &&
    !!settings.returns?.enableGiftReceipts;

  const [errorCode, setErrorCode] = useState<ConvertTokenError | undefined>(
    undefined,
  );
  const [purchaseChannel, setPurchaseChannel] = useState<
    PurchaseChannel | undefined
  >(undefined);
  const [channelName, setChannelName] = useState<string>("Unknown");

  const [currentPage, setCurrentPage] = useState(LoginPage.LANDING_PAGE);
  const isPortalDisabled =
    !settings.active || settings.returns?.enableCustomerPortal === false;

  const giftButtonClicked = useHandler(() => {
    setCurrentPage(LoginPage.GIFT);
  });

  const onBackGiftClicked = useHandler(() => {
    setCurrentPage(LoginPage.LANDING_PAGE);
  });

  const emailButtonClicked = useHandler(() => {
    setCurrentPage(LoginPage.EMAIL_LINK);
  });

  const onBackZipcodeClicked = useHandler(() => {
    setCurrentPage(LoginPage.LANDING_PAGE);
  });

  const registerButtonClicked = useHandler(() => {
    if (settings.warranties?.portalFourRegistration) {
      setCurrentPage(LoginPage.REGISTER);
    } else {
      navigate("/returns-portal/warranties/register", { replace: true });
    }
  });

  const onBackRegisterClicked = useHandler(() => {
    setCurrentPage(LoginPage.LANDING_PAGE);
  });

  const toOnlineRegistration = useHandler((channelName: string) => {
    setCurrentPage(LoginPage.REGISTER_OTP);
    setPurchaseChannel(PurchaseChannel.ONLINE);
    setChannelName(channelName);
  });

  const toRetailRegistration = useHandler((channelName: string) => {
    setCurrentPage(LoginPage.REGISTER_OTP);
    setPurchaseChannel(PurchaseChannel.RETAIL);
    setChannelName(channelName);
  });

  const onBackRegisterOtpClicked = useHandler(() => {
    setCurrentPage(LoginPage.REGISTER);
  });

  const emailToken = params.get("email_token");
  const exchangeConversionEmailToken = params.get(
    "exchange_conversion_email_token",
  );
  const orderTrackingToken = params.get("order_tracking_token");
  const boxlessReturnsLocationsToken = params.get(
    "boxless_returns_locations_token",
  );
  const fedexEasyReturnsLocationsToken = params.get(
    "fedex_easy_returns_locations_token",
  );
  const orderTrackingFulfillmentIndex = params.get(
    "order_tracking_fulfillment_index",
  );
  const orderTrackingType = params.get("trackable_type") || "order";

  const customer = params.get("customer");
  const giftReceiptId = params.get("gift_receipt_id");
  const token = params.get("token");
  const next = params.get("next");
  const isMerchantCreatedReturn =
    params.get("isMerchantCreatedReturn") === "true";
  const merchantName = params.get("merchantName");

  const email = params.get("email");
  const zip = params.get("zip");
  const order = params.get("order");

  const registerOnly = params.get("register_only");

  // Initialize the current page based on the search params
  useEffect(() => {
    setCurrentPage(getInitialPage(params));
  }, [params]);

  useEffect(() => {
    if (!customer || !token || !setAuth_ || !setUser || !rpcClient) {
      return;
    }
    setAuth_(token);
    setUser({
      ...JSON.parse(customer),
      isMerchant: isMerchantCreatedReturn,
      merchantName,
    });
    const url = new URL(location.href);
    url.searchParams.delete("customer");
    url.searchParams.delete("token");
    url.searchParams.delete("merchantName");
    const path = next || "/returns-portal/orders";
    navigate(path, { replace: true });
  }, [
    customer,
    token,
    next,
    isMerchantCreatedReturn,
    merchantName,
    navigate,
    rpcClient,
    setAuth_,
    setUser,
  ]);

  //Auto login for third party customer accounts with email and token
  useEffect(() => {
    if (!rpcClient || !setAuth_ || !setUser) {
      return;
    }
    if (giftReceiptId) {
      void rpcClient
        .giftReceiptAuth({ giftReceiptId })
        .then(({ customerAuthToken, orderId, gifterName, gifterEmail }) => {
          setAuth_(customerAuthToken);
          setUser({ name: gifterName, email: gifterEmail ?? "" });
          navigate("/returns-portal/orders", { replace: true });
        });
      return;
    }
    const emailOrZip = email || zip;
    if (emailOrZip && order) {
      void rpcClient
        .singleOrderAuth({ emailOrZip, orderIdTrackingNumber: order })
        .then(({ customerAuthToken, customer, orderId }) => {
          setAuth_(customerAuthToken);
          setUser(customer ?? undefined);
          let path: string;
          if (orderTrackingType && orderTrackingType === "order") {
            path =
              next ||
              (orderTrackingFulfillmentIndex &&
              orderTrackingFulfillmentIndex !== "undefined"
                ? `/returns-portal/orders/${orderId}/track/${orderTrackingFulfillmentIndex}`
                : `/returns-portal/orders/${orderId}`);
          } else if (orderTrackingType && orderTrackingType !== "order") {
            path =
              next ||
              `/returns-portal/orders/${orderId}?redirect_to_return=true`;
          } else {
            path = next || `/returns-portal/orders/${orderId}`;
          }
          if (path.includes("{orderId}")) {
            path = path.replace("{orderId}", orderId);
          }
          navigate(path, { replace: true });
        })
        .catch((error) => {
          console.error("Auto-login failed:", error);
        });
    }
  }, [
    orderTrackingType,
    orderTrackingFulfillmentIndex,
    email,
    zip,
    order,
    giftReceiptId,
    next,
    rpcClient,
    setAuth_,
    setUser,
    navigate,
  ]);

  //Auto login for email and order tracking token
  useEffect(() => {
    if (!rpcClient || !setAuth_ || !setUser) {
      return;
    }

    const handleError = (e: any) => {
      const code = e.code as ConvertTokenError | undefined;
      if (code) {
        setErrorCode(code);
      }
    };
    if (emailToken) {
      void rpcClient
        .convertTokenToCustomerAuth({ emailToken })
        .then(({ customerAuthToken, customer }) => {
          setAuth_(customerAuthToken);
          setUser(customer);
          const path = next || "/returns-portal/orders";
          navigate(path, { replace: true });
        })
        .catch(handleError);
    }
    if (exchangeConversionEmailToken) {
      void rpcClient
        .convertTokenToCustomerAuth({ exchangeConversionEmailToken })
        .then(({ customerAuthToken, customer }) => {
          setAuth_(customerAuthToken);
          setUser(customer);
          const path = next || "/returns-portal/orders";
          navigate(path, { replace: true });
        })
        .catch(handleError);
    }
    if (orderTrackingToken) {
      void rpcClient
        .convertTokenToCustomerAuth({ orderTrackingToken })
        .then(({ customerAuthToken, customer, orderId }) => {
          setAuth_(customerAuthToken);
          setUser(customer);
          if (orderTrackingType === "order") {
            navigate(
              orderTrackingFulfillmentIndex &&
                orderTrackingFulfillmentIndex !== "undefined"
                ? `/returns-portal/orders/${orderId}/track/${orderTrackingFulfillmentIndex}`
                : `/returns-portal/orders/${orderId}`,
              { replace: true },
            );
          } else {
            navigate(
              `/returns-portal/orders/${orderId}?redirect_to_return=true`,
              { replace: true },
            );
          }
        })
        .catch(handleError);
    }
    if (boxlessReturnsLocationsToken) {
      void rpcClient
        .convertBoxlessTokenToCustomerAuth({ boxlessReturnsLocationsToken })
        .then(({ customerAuthToken, returnId }) => {
          setAuth_(customerAuthToken);
          const path =
            next ||
            `/returns-portal/returns/${returnId}?boxlessReturnsLocations=true`;
          navigate(path, { replace: true });
        })
        .catch(handleError);
    }
    if (fedexEasyReturnsLocationsToken) {
      void rpcClient
        .convertFedexTokenToCustomerAuth({ fedexEasyReturnsLocationsToken })
        .then(({ customerAuthToken, returnId }) => {
          setAuth_(customerAuthToken);
          const path =
            next ||
            `/returns-portal/returns/${returnId}?fedExEasyReturnsLocations=true`;
          navigate(path, { replace: true });
        })
        .catch(handleError);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailToken,
    exchangeConversionEmailToken,
    orderTrackingToken,
    boxlessReturnsLocationsToken,
    fedexEasyReturnsLocationsToken,
  ]);

  return {
    settings,
    isPortalDisabled,
    currentPage,
    purchaseChannel,
    channelName,
    giftButtonClicked,
    emailButtonClicked,
    registerButtonClicked,
    toRetailRegistration,
    toOnlineRegistration,
    // No back button for register only
    onBackRegisterClicked: registerOnly ? undefined : onBackRegisterClicked,
    onBackZipcodeClicked,
    onBackGiftClicked,
    onBackRegisterOtpClicked,
    isMobile: screen === ScreenType.MOBILE,
    errorMessage: errorCode ? errorCodeToMessage[errorCode] : undefined,
    isCreateGiftReturnLink,
  };
}
